import streamUrlUtil from './streamUrlUtil';
import debug from './debugger';
import tuner from './tuner';
import deferred from './utils/deferredPromise';
import cancelable from './utils/cancelable';
import getMasterManifestUrl from './utils/getMasterManifestUrl';
import mediaFormats from './player/constants/mediaFormats';
import playerName from './player/constants/playerName';
import { sendTimingEvent } from './loggers/timing';

let currentStream;
let processedDeferred;
let streamPlayerPromise;
let masterManifestFetchPromise;
const defaultShoutcastExtraTimeout = 10000; // default - should be derived from config

function onSuccess(connectionData) {
  debug.log('playing');

  processedDeferred.resolve(connectionData);
}

function onFailure(err) {
  debug.log('failed', err);

  processedDeferred.reject(err);
}

function attemptPlayback(playbackConfig) {
  // TODO: remove if statement after manifest fetching logic is moved from process() below to
  //  InstreamAdPlayer
  if (!currentStream.isHlsAdvanced) {
    tuner.trigger(tuner.events.attemptingPlayback);
  }

  return cancelable(tuner.streamPlayer.attemptPlayback(playbackConfig));
}

function attemptShoutcastStream(err) {
  const shoutcastUrl = streamUrlUtil.shoutcastStreamUrl(currentStream.url);

  if (shoutcastUrl !== currentStream.url) {
    currentStream.url = shoutcastUrl;
    currentStream.isShoutcastHack = true;

    debug.log('attempting shoutcast', currentStream);

    const playbackConfig = {
      stream: currentStream,
      ignoreStreamPosition: false,
      additionalTimeout:
        currentStream.isFinalStream &&
        (tuner.shoutcastExtraTimeout || defaultShoutcastExtraTimeout),
    };
    const cancelablePromise = attemptPlayback(playbackConfig);

    cancelablePromise.then(onSuccess).catch((error) => {
      // if the test stream fails, clear media so requests don't hang
      if (tuner.tune.testStreamUrl && currentStream.isFinalStream) {
        tuner.streamPlayer.clearMedia();
      }

      onFailure(error);
    });

    streamPlayerPromise = cancelablePromise;
  } else {
    onFailure(err);
  }
}

export default {
  process: async function (stream) {
    const originalUrl = stream.url;

    currentStream = stream;

    // Apply sanitization routines if allowed to do so
    if (stream.format !== mediaFormats.hls) {
      // If the stream's configured format is not hls, but the stream url has an hls
      // extension, then we need to report this stream url so that we can get the format
      // updated correctly.
      if (streamUrlUtil.isHLS(currentStream.url)) {
        currentStream.isHlsFormatIncorrect = true;
      }
      // If the url is a .m3u, remove the extension and indicate the stream had the .m3u hack
      // applied for logging purposes
      currentStream.url = streamUrlUtil.removeM3UExtension(currentStream.url);
      if (originalUrl !== currentStream.url) {
        currentStream.removedM3UExtension = true;
      }
    }

    processedDeferred = deferred();

    if (stream.forceShoutcast) {
      attemptShoutcastStream(null);
    } else {
      debug.log('attempting playback', currentStream);

      const playbackConfig = {
        stream: currentStream,
        ignoreStreamPosition: tuner.tune.ignoreStreamPosition,
        playFromPosition: tuner.tune.playFromPosition,
        liveSeekStreamPosition: tuner.tune.liveSeekStreamPosition,
      };

      if (currentStream.isHlsAdvanced) {
        // TODO: move this if-statement logic to HLS player load method.
        //  The arbitrary emitting of `attemptingPlayback` and `playerSelected` below can be
        //  removed after that, since StreamPlayer will have already emitted the event before any
        //  media is loaded
        tuner.trigger(tuner.events.attemptingPlayback);
        tuner.trigger(tuner.events.playerSelected, playerName.InstreamAdPlayer);
        const getMasterManifestTimingStart = performance.now();
        masterManifestFetchPromise = cancelable(
          getMasterManifestUrl(currentStream.url),
        ).then((result) => {
          sendTimingEvent('getMasterManifestUrl', getMasterManifestTimingStart);
          return result;
        });
        const { trackingUrl, manifestUrl } = await masterManifestFetchPromise;

        currentStream.url = manifestUrl;
        playbackConfig.instreamTrackingUrl = trackingUrl;
      }

      streamPlayerPromise = attemptPlayback(playbackConfig);

      // chaining separately so that streamPlayerPromise retains references to cancelable API
      streamPlayerPromise
        .then(onSuccess)
        .catch((err) => attemptShoutcastStream(err));
    }

    return processedDeferred.promise;
  },
  processTransitionStream: async function ({
    stream,
    isBoostTransition,
    transitionStreamName,
  }) {
    try {
      await tuner.streamPlayer.attemptPlayback({
        stream: {
          url: stream || '',
          format: mediaFormats.mp3,
          isBoostTransition,
          transitionStreamName,
        },
      });

      tuner.changeState(tuner.states.playing);
    } catch (e) {
      throw new Error(`Error playing ${transitionStreamName} stream`);
    }
  },
  cancel: function () {
    tuner.streamPlayer.clearTimeout();

    if (masterManifestFetchPromise) masterManifestFetchPromise.cancel();
    if (streamPlayerPromise) streamPlayerPromise.cancel();
    if (processedDeferred) processedDeferred.promise.cancel();
  },
};
